// all mostly copied from:
// https://github.com/ecies/js/blob/a85bc84d503556e1ed149a03e01daf4a029d53c9/src/config.ts#L14-L44 (& associated files)
// & altered to add bulk functionality and remove Buffer dependency
// may make it difficult to update, but there are tests + typescript should catch regressions as well

// @ts-ignore
import { aes256cbc, aes256gcm } from '@ecies/ciphers/aes';
// @ts-ignore
import { xchacha20 } from '@ecies/ciphers/chacha';
import { type Cipher, concatBytes } from '@noble/ciphers/utils';
import { ECIES_CONFIG, PrivateKey, PublicKey } from 'eciesjs';

// elliptic
// const SECRET_KEY_LENGTH = 32;
const COMPRESSED_PUBLIC_KEY_SIZE = 33;
const UNCOMPRESSED_PUBLIC_KEY_SIZE = 65;
// const ETH_PUBLIC_KEY_SIZE = 64;
const CURVE25519_PUBLIC_KEY_SIZE = 32;

// symmetric
const XCHACHA20_NONCE_LENGTH = 24;
const AEAD_TAG_LENGTH = 16;

ECIES_CONFIG.ellipticCurve = 'x25519';
ECIES_CONFIG.symmetricAlgorithm = 'xchacha20';

// const ellipticCurve = () => ECIES_CONFIG.ellipticCurve;
const isEphemeralKeyCompressed = () => ECIES_CONFIG.isEphemeralKeyCompressed;
const isHkdfKeyCompressed = () => ECIES_CONFIG.isHkdfKeyCompressed;
const symmetricAlgorithm = () => ECIES_CONFIG.symmetricAlgorithm;
const symmetricNonceLength = () => ECIES_CONFIG.symmetricNonceLength;

const ephemeralKeySize = () => {
  const mapping = {
    secp256k1: ECIES_CONFIG.isEphemeralKeyCompressed
      ? COMPRESSED_PUBLIC_KEY_SIZE
      : UNCOMPRESSED_PUBLIC_KEY_SIZE,
    x25519: CURVE25519_PUBLIC_KEY_SIZE,
    ed25519: CURVE25519_PUBLIC_KEY_SIZE
  };

  if (ECIES_CONFIG.ellipticCurve in mapping) {
    return mapping[ECIES_CONFIG.ellipticCurve];
  }
  throw new Error('Not implemented');
};

export function hexToUint8Array(hexString: string): Uint8Array {
  const bytes = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hexString.substring(i, i + 2), 16);
  }

  return bytes;
}

export function uint8ArrayToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const HEX_REGEX = /^[0-9A-Fa-f]+$/;
export function validateAndDecryptMessage(
  hexString: string,
  secretKey: string
) {
  const decoder = new TextDecoder();

  let unencrypted;
  if (!HEX_REGEX.test(hexString) || hexString.length % 2 !== 0) {
    throw new Error('Invalid hexadecimal string');
  }

  const bytes = hexToUint8Array(hexString);

  try {
    unencrypted = decrypt(secretKey, bytes);
  } catch (error) {
    console.error('Failed to decrypt content:', error);
    throw new Error('Invalid content');
  }
  const trimmed = decoder.decode(unencrypted).trim();
  if (trimmed.length < 1) {
    throw new Error('The message is too short');
  }
  if (trimmed.length > 10 * 1024) {
    throw new Error('The message is too long');
  }

  return trimmed;
}

export function skToPk(sk: string): string {
  const privateKey = PrivateKey.fromHex(sk);
  return privateKey.publicKey.toHex();
}

export function decryptMessages<T extends { contentEnc: string }>(
  messages: T[],
  decryptionKey: string
): (Omit<T, 'contentEnc'> & { content: string })[] {
  const decoder = new TextDecoder();
  const decryptedMessages = decrypt(
    decryptionKey,
    messages.map((message) =>
      hexToUint8Array(
        message.contentEnc.startsWith('\\x')
          ? message.contentEnc.slice(2)
          : message.contentEnc
      )
    )
  ).map((message) => decoder.decode(message));

  return messages.map((message, i) => {
    const { contentEnc: _contentEnc, ...restMessage } = message;

    return {
      ...restMessage,
      content: decryptedMessages[i]
    };
  });
}

// https://github.com/ecies/js/blob/a85bc84d503556e1ed149a03e01daf4a029d53c9/src/index.ts#L32C1-L72C1
// using our own custom versions to avoid `Buffer` dependency, plus we added bulk encryption/decryption
export function encrypt(
  receiverRawPK: string | Uint8Array,
  data: Uint8Array
): Uint8Array;
export function encrypt(
  receiverRawPK: string | Uint8Array,
  dataArray: Uint8Array[]
): Uint8Array[];
export function encrypt(
  receiverRawPK: string | Uint8Array,
  data: Uint8Array | Uint8Array[]
): Uint8Array | Uint8Array[] {
  const ephemeralSK = new PrivateKey(randomBytes(32));
  const receiverPK =
    receiverRawPK instanceof Uint8Array
      ? new PublicKey(receiverRawPK)
      : PublicKey.fromHex(receiverRawPK);

  const sharedKey = ephemeralSK.encapsulate(receiverPK, isHkdfKeyCompressed());
  const ephemeralPK = ephemeralSK.publicKey.toBytes(isEphemeralKeyCompressed());

  if (Array.isArray(data)) {
    return data.map((item) => {
      const encrypted = symEncrypt(sharedKey, item);
      return concatBytes(ephemeralPK, encrypted);
    });
  }
  const encrypted = symEncrypt(sharedKey, data);
  return concatBytes(ephemeralPK, encrypted);
}

export function decrypt(
  receiverRawSK: string | Uint8Array,
  data: Uint8Array
): Uint8Array;
export function decrypt(
  receiverRawSK: string | Uint8Array,
  dataArray: Uint8Array[]
): Uint8Array[];
export function decrypt(
  receiverRawSK: string | Uint8Array,
  data: Uint8Array | Uint8Array[]
): Uint8Array | Uint8Array[] {
  const receiverSK =
    receiverRawSK instanceof Uint8Array
      ? new PrivateKey(receiverRawSK)
      : PrivateKey.fromHex(receiverRawSK);

  const keySize = ephemeralKeySize();

  if (Array.isArray(data)) {
    return data.map((item) => {
      const ephemeralPK = new PublicKey(item.subarray(0, keySize));
      const encrypted = item.subarray(keySize);
      const sharedKey = ephemeralPK.decapsulate(
        receiverSK,
        isHkdfKeyCompressed()
      );
      return symDecrypt(sharedKey, encrypted);
    });
  }
  const ephemeralPK = new PublicKey(data.subarray(0, keySize));
  const encrypted = data.subarray(keySize);
  const sharedKey = ephemeralPK.decapsulate(receiverSK, isHkdfKeyCompressed());
  return symDecrypt(sharedKey, encrypted);
}

const symEncrypt = (
  key: Uint8Array,
  plainText: Uint8Array,
  AAD?: Uint8Array
): Uint8Array => _exec(_encrypt, key, plainText, AAD);

const symDecrypt = (
  key: Uint8Array,
  cipherText: Uint8Array,
  AAD?: Uint8Array
): Uint8Array => _exec(_decrypt, key, cipherText, AAD);

function _exec(
  callback: typeof _encrypt | typeof _decrypt,
  key: Uint8Array,
  data: Uint8Array,
  AAD?: Uint8Array
): Uint8Array {
  const algorithm = symmetricAlgorithm();
  if (algorithm === 'aes-256-gcm') {
    return callback(
      aes256gcm,
      key,
      data,
      symmetricNonceLength(),
      AEAD_TAG_LENGTH,
      AAD
    );
  }
  if (algorithm === 'xchacha20') {
    return callback(
      xchacha20,
      key,
      data,
      XCHACHA20_NONCE_LENGTH,
      AEAD_TAG_LENGTH,
      AAD
    );
  }
  if (algorithm === 'aes-256-cbc') {
    // NOT RECOMMENDED. There is neither AAD nor AEAD tag in cbc mode
    // aes-256-cbc always uses 16 bytes iv
    return callback(aes256cbc, key, data, 16, 0);
  }
  throw new Error('Not implemented');
}

function randomBytes(length: number): Uint8Array {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return array;
}

function _encrypt(
  func: (key: Uint8Array, nonce: Uint8Array, AAD?: Uint8Array) => Cipher,
  key: Uint8Array,
  data: Uint8Array,
  nonceLength: 12 | 16 | 24,
  tagLength: 16 | 0,
  AAD?: Uint8Array
): Uint8Array {
  const nonce = randomBytes(nonceLength);
  const cipher = func(key, nonce, AAD);
  // @noble/ciphers format: cipherText || tag
  const encrypted = cipher.encrypt(data);

  if (tagLength === 0) {
    return concatBytes(nonce, encrypted);
  }

  const cipherTextLength = encrypted.length - tagLength;
  const cipherText = encrypted.subarray(0, cipherTextLength);
  const tag = encrypted.subarray(cipherTextLength);
  // ecies payload format: pk || nonce || tag || cipherText
  return concatBytes(nonce, tag, cipherText);
}

function _decrypt(
  func: (key: Uint8Array, nonce: Uint8Array, AAD?: Uint8Array) => Cipher,
  key: Uint8Array,
  data: Uint8Array,
  nonceLength: 12 | 16 | 24,
  tagLength: 16 | 0,
  AAD?: Uint8Array
): Uint8Array {
  const nonce = data.subarray(0, nonceLength);
  const cipher = func(key, Uint8Array.from(nonce), AAD); // to reset byteOffset
  const encrypted = data.subarray(nonceLength);

  if (tagLength === 0) {
    return cipher.decrypt(encrypted);
  }

  const tag = encrypted.subarray(0, tagLength);
  const cipherText = encrypted.subarray(tagLength);
  return cipher.decrypt(concatBytes(cipherText, tag));
}
